import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import moment from "moment-timezone";

import {
  DATE_FORMAT,
  DATETIME_ISO,
  TIME_FORMAT,
  TIME_FORMAT_ISO,
  ComplianceUIDateTimeFormat,
} from "@common/constants/datetime";

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);

/**
 * Get 1 hour from the current time.
 * @returns {Date} A Date object representing 1 hour ahead of the current time.
 */
export function anHourFromNow() {
  return dayjs().add(1, "hour").toDate();
}

export function getTomorrow() {
  return dayjs().add(1, "day").toDate();
}

/**
 * Get midnight of the previous day.
 * @returns {Date} A Date object representing midnight of the previous day.
 */
export function getMidnightPreviousDay() {
  return dayjs().subtract(1, "day").startOf("day").toDate();
}

export function getSevenDaysInFuture() {
  return dayjs().add(7, "day").startOf("day").toDate();
}

export function getSevenDaysInPast() {
  const datetime = dayjs().subtract(7, "day").startOf("day");
  return formatToGMTISO(datetime);
}

export function getThreeDaysInPast() {
  const datetime = dayjs().subtract(3, "day").startOf("day");
  return formatToGMTISO(datetime);
}

export function get24HoursInPast() {
  const datetime = dayjs().subtract(24, "hour").startOf("hour");
  return formatToGMTISO(datetime);
}

export function getOneMonthInPast() {
  const datetime = dayjs().subtract(1, "month").startOf("day");
  return formatToGMTISO(datetime);
}

export const getSameDateLastYear = (date = dayjs()) => {
  return dayjs(date).subtract(1, "year").format("YYYY-MM-DD");
};

/**
 * Get the current date and time in GMT (accouting for BST + 1).
 * @returns {Date} A Date object representing the current date and time.
 */
export function getCurrentDateTime() {
  return formatToGMTISO(dayjs());
}

/**
 * Get the date part of a Date object.
 * @param {Date} date - The date object to extract the date part from.
 * @param {string} [dateFormat="YYYY-MM-DD"] - The format to use for the date. Defaults to "YYYY-MM-DD".
 * @returns {string} The formatted date string.
 */
export function getDatePart(date, dateFormat = DATE_FORMAT) {
  return dayjs(date).format(dateFormat);
}

/**
 * Get the time part of a Date object.
 * @param {Date} date - The date object to extract the time part from.
 * @returns {string} The formatted time string in "HH:mm" format.
 */
export function getTimePart(date) {
  return dayjs(date).format(TIME_FORMAT);
}

/**
 * Get the time part of a Date object formatted into hh:mm:ss.
 * @param {Date} date - The date object to extract the time part from.
 * @returns {string} The formatted time string in "HH:mm:ss" format.
 */
export function getISOTime(date) {
  return dayjs(date).format(TIME_FORMAT_ISO);
} /**

 * Convert a date and time to ISO 8601 format, accounting for BST (UTC+1).
 * @param {string} date - The date string.
 * @param {string} time - The time string.
 * @returns {string} The ISO 8601 formatted date-time string, or null if invalid.
 */
export function convertToIsoDateTime(date, time) {
  if (!date || !time) {
    return null;
  }
  const datetime = dayjs.tz(`${date} ${time}`, "Europe/London");

  if (!datetime.isValid()) {
    return null;
  }

  return datetime.format("YYYY-MM-DDTHH:mm:ss.SSSZ");
}

/**
 * Validate a date string against a given format.
 * @param {string} date - The date string to validate.
 * @param {string} [dateFormat="YYYY-MM-DD"] - The format to validate against, ie."YYYY-MM-DD".
 * @returns {boolean} True if the date is valid, false otherwise.
 */
export function validateDate(date, dateFormat = DATE_FORMAT) {
  // Validate the date
  const isDateValid = dayjs(date, dateFormat, true).isValid();
  return isDateValid;
}

/**
 * Validate a time string in "HH:mm" format.
 * @param {string} time - The time string to validate.
 * @returns {boolean} True if the time is valid, false otherwise.
 */
export function validateTime(time) {
  // Validate the time
  const isTimeValid = dayjs(time, TIME_FORMAT, true).isValid();
  return isTimeValid;
}

export function validateTimeNotInFuture(time, date) {
  // Skip if no date selected yet
  if (!date || !time) return true;

  // Validate combined date and time
  const selectedDateTime = dayjs(`${date} ${time}`);
  const now = dayjs();

  return !selectedDateTime.isAfter(now);
}

/**
 * Get the year difference between now and the given date.
 * @param {string} date - The data string to validate.
 * @returns {int} The number of years as an int.
 */
export function yearDifference(date) {
  return dayjs().diff(dayjs(date), "year");
}

/**
 * Returns the the unix epoch in seconds.
 * @returns {int} epoch.
 */
export function epochInSeconds() {
  return Math.floor(Date.now() / 1000);
}

export const formatDate = (date, format = DATE_FORMAT) => {
  return date ? dayjs(date).format(format) : "N/A";
};

/**
 * Convert seconds to ISO Time usage examples:
 *
 * convertSecondsToISOTime(null);   // "-"
 * convertSecondsToISOTime("");     // "-"
 * convertSecondsToISOTime();       // "-"
 * convertSecondsToISOTime(0);      // "00:00:00"
 * convertSecondsToISOTime(3661);   // "01:01:01"
 */
export const convertSecondsToISOTime = (seconds) => {
  // Check for null or undefined (but allow 0)
  if (seconds === null || seconds === undefined || seconds === "") return "-";
  // Ensure seconds is a number
  const sec = Number(seconds);
  if (isNaN(sec) || sec < 0) return "-";
  return new Date(sec * 1000).toISOString().substr(11, 8);
};

// Convert ISO datetime to GMT using moment on CMS Desktop
export const formatDateTimeToGMT = (datetime) =>
  moment.utc(datetime).tz("Europe/London").format(ComplianceUIDateTimeFormat);

// Convert ISO datetime to GMT using dayjs on Mobile
export const formatDayjsDateTimeToGMT = (datetime) => {
  if (!datetime) return null;
  return datetime.tz("Europe/London").format(ComplianceUIDateTimeFormat);
};

// Convert to ISO
export const formatToGMTISO = (datetime) => {
  if (!datetime) return null;
  return datetime.tz("Europe/London").format(DATETIME_ISO);
};

export const setComplianceFormDateTime = (
  setValue = () => {},
  eventType = "",
) => {
  switch (eventType) {
    case "Customer Interaction":
      setValue("interactionDate", getDatePart(getCurrentDateTime()));
      setValue("interactionTime", getTimePart(getCurrentDateTime()));
      break;
    case "Observations":
      setValue("observationDate", getDatePart(getCurrentDateTime()));
      setValue("observationTime", getTimePart(getCurrentDateTime()));
      break;
    default:
      setValue("date", getDatePart(getCurrentDateTime()));
      setValue("time", getTimePart(getCurrentDateTime()));
      break;
  }
};

// Get the date according to different compliance event types
export const getEventDate = (row) => {
  const fields = [
    row.customerInteraction?.interactionDate,
    row.observation?.observationDate,
    row.incident?.incidentDate,
    row.ageVerification?.ageVerificationDate,
    row.agcCheck?.timeChecked,
    row.dateCreated,
  ];

  for (const field of fields) {
    if (field) {
      return getFormattedDate(field);
    }
  }

  return "N/A";
};

export const getFormattedDate = (date) => {
  return moment(date).format(ComplianceUIDateTimeFormat);
};

export const fromDateTime = (dateRange) => {
  if (!dateRange) return null;
  return `${dateRange.split(" - ")[0]}T00:00:00Z`;
};

export const toDateTime = (dateRange) => {
  if (!dateRange) return null;
  return `${dateRange.split(" - ")[1]}T23:59:59Z`;
};

export const fromDate = (dateRange) => {
  if (!dateRange) return null;
  return dateRange.split(" - ")[0];
};

export const toDate = (dateRange) => {
  if (!dateRange) return null;
  return dateRange.split(" - ")[1];
};

export const formatSecondsToTime = (seconds) => {
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};
